import { Button, Icon, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  regionPublications,
  type CreateRegionPublicationRequest,
  type PublicationOption,
  type RegionOption,
  type RegionPublicationRow,
} from "../../api/region-publications.service.js";
import EmptyState from "../../components/EmptyState/index.js";
import LoadingState from "../../components/LoadingState/index.js";
import { PageHeader } from "../../components/PageHeader/index.js";
import {
  SearchableSelect,
  type SelectOption,
} from "../../components/Form/SearchableSelect/index.js";
import styles from "./RegionPublications.module.css";

const PHASE_OPTIONS: SelectOption[] = [
  { value: "current", label: "Current" },
  { value: "next", label: "Next" },
  { value: "archive", label: "Archive" },
];

type AddMappingFormState = {
  regionId: string | number | null;
  phase: string | number | null;
  publicationId: string | number | null;
};

const EMPTY_FORM: AddMappingFormState = {
  regionId: null,
  phase: null,
  publicationId: null,
};

type RegionGroup = {
  regionId: string;
  regionLabel: string;
  rows: RegionPublicationRow[];
};

function groupRowsByRegion(rows: RegionPublicationRow[]): RegionGroup[] {
  const groupMap = new Map<string, RegionGroup>();

  for (const row of rows) {
    const existing = groupMap.get(row.regionId);
    if (existing) {
      existing.rows.push(row);
    } else {
      groupMap.set(row.regionId, {
        regionId: row.regionId,
        regionLabel: row.regionLabel,
        rows: [row],
      });
    }
  }

  return Array.from(groupMap.values());
}

function PhaseBadge({ phase }: { phase: "current" | "next" | "archive" }) {
  const phaseClass =
    phase === "current"
      ? styles.phaseCurrent
      : phase === "next"
        ? styles.phaseNext
        : styles.phaseArchive;
  return <span className={`${styles.phaseBadge} ${phaseClass}`}>{phase}</span>;
}

function MappingRow({
  row,
  onRemove,
}: {
  row: RegionPublicationRow;
  onRemove: (id: number) => void;
}) {
  const handleRemove = useCallback(() => onRemove(row.id), [row.id, onRemove]);

  return (
    <div className={styles.mappingRow}>
      <span>{row.publicationName}</span>
      <PhaseBadge phase={row.phase} />
      <span className={styles.statusText}>{row.publicationStatus}</span>
      <span></span>
      <button
        type="button"
        className={styles.removeButton}
        onClick={handleRemove}
        aria-label={`Remove ${row.publicationName} (${row.phase})`}
        title="Remove mapping"
      >
        <Icon icon="trash" size="small" />
      </button>
    </div>
  );
}

function RegionGroupSection({
  group,
  onRemove,
}: {
  group: RegionGroup;
  onRemove: (id: number) => void;
}) {
  return (
    <div className={styles.regionGroup}>
      <div className={styles.regionHeader}>
        <span className={styles.regionLabel}>{group.regionLabel}</span>
        <span className={styles.rowCount}>
          {group.rows.length} mapping{group.rows.length !== 1 ? "s" : ""}
        </span>
      </div>
      {group.rows.length === 0 ? (
        <div className={styles.emptyRegion}>No mappings</div>
      ) : (
        group.rows.map(row => <MappingRow key={row.id} row={row} onRemove={onRemove} />)
      )}
    </div>
  );
}

function AddMappingModal({
  isOpen,
  onClose,
  onSuccess,
  regionOptions,
  publicationOptions,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  regionOptions: RegionOption[];
  publicationOptions: PublicationOption[];
}) {
  const [form, setForm] = useState<AddMappingFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const regionSelectOptions = useMemo<SelectOption[]>(
    () => regionOptions.map(r => ({ value: r.id, label: r.label })),
    [regionOptions],
  );

  const publicationSelectOptions = useMemo<SelectOption[]>(
    () =>
      publicationOptions.map(p => ({
        value: p.id,
        label: p.name,
        description: p.status,
      })),
    [publicationOptions],
  );

  function resetForm() {
    setForm(EMPTY_FORM);
    setFormError(null);
  }

  function handleClose() {
    if (!submitting) {
      resetForm();
      onClose();
    }
  }

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  async function handleSubmit() {
    if (!form.regionId || !form.phase || !form.publicationId) {
      setFormError("All fields are required.");
      return;
    }

    setFormError(null);
    setSubmitting(true);

    try {
      const payload: CreateRegionPublicationRequest = {
        region_id: String(form.regionId),
        phase: form.phase as "current" | "next" | "archive",
        publication_id: Number(form.publicationId),
      };
      await regionPublications.create(payload);
      resetForm();
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create mapping";
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Region Publication Mapping"
      size="medium"
    >
      <div className={styles.modalFields}>
        <SearchableSelect
          name="region"
          label="Region"
          required
          value={form.regionId}
          options={regionSelectOptions}
          onChange={value => setForm(prev => ({ ...prev, regionId: value }))}
          placeholder="Select a region..."
        />
        <SearchableSelect
          name="phase"
          label="Phase"
          required
          value={form.phase}
          options={PHASE_OPTIONS}
          onChange={value => setForm(prev => ({ ...prev, phase: value }))}
          placeholder="Select phase..."
        />
        <SearchableSelect
          name="publication"
          label="Edition"
          required
          value={form.publicationId}
          options={publicationSelectOptions}
          onChange={value => setForm(prev => ({ ...prev, publicationId: value }))}
          placeholder="Search editions..."
        />
      </div>

      {formError && <p className={styles.modalError}>{formError}</p>}

      <div className={styles.modalActions}>
        <Button variant="outlined" color="default" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Adding..." : "Add Mapping"}
        </Button>
      </div>
    </Modal>
  );
}

export default function RegionPublicationsPage() {
  const [rows, setRows] = useState<RegionPublicationRow[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [publicationOptions, setPublicationOptions] = useState<PublicationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const regionGroups = useMemo(() => groupRowsByRegion(rows), [rows]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [rowData, optionsData] = await Promise.all([
        regionPublications.getAll(),
        regionPublications.getOptions(),
      ]);
      setRows(rowData);
      setRegionOptions(optionsData.regions);
      setPublicationOptions(optionsData.publications);
    } catch (err) {
      setLoadError(err instanceof Error ? err : new Error("Failed to load data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRemoveMapping = useCallback(
    async (id: number) => {
      try {
        await regionPublications.delete(id);
        setToast({ message: "Mapping removed", type: "success" });
        await loadData();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to remove mapping";
        setToast({ message, type: "error" });
      }
    },
    [loadData],
  );

  const handleAddSuccess = useCallback(async () => {
    setIsAddModalOpen(false);
    setToast({ message: "Mapping added", type: "success" });
    await loadData();
  }, [loadData]);

  if (loading) {
    return <LoadingState message="Loading region publications..." />;
  }

  if (loadError) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load region publications"
        message={loadError.message}
        action={{ label: "Retry", onClick: loadData }}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Region Publications"
        subtitle="Explicit edition mapping per region and phase — replaces taxonomy derivation"
      >
        <div className={styles.headerActions}>
          <Button variant="contained" color="primary" onClick={() => setIsAddModalOpen(true)}>
            <Icon icon="plus" size="small" />
            Add Mapping
          </Button>
        </div>
      </PageHeader>

      <div className={styles.tableSection}>
        {regionGroups.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No mappings"
            message="Add a mapping to associate a region with a publication edition."
          />
        ) : (
          <>
            <div className={styles.colHeader}>
              <span>Edition</span>
              <span>Phase</span>
              <span>Status</span>
              <span></span>
              <span></span>
            </div>
            {regionGroups.map(group => (
              <RegionGroupSection
                key={group.regionId}
                group={group}
                onRemove={handleRemoveMapping}
              />
            ))}
          </>
        )}
      </div>

      <AddMappingModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        regionOptions={regionOptions}
        publicationOptions={publicationOptions}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={4000}
        />
      )}
    </div>
  );
}
